/**
 * HTTP/SSE client for the embed surface: POST /embed/sessions bootstrap,
 * authenticated /interviews/{id}/* calls, and the SSE event stream
 * (EventSource can't set headers, so the token rides as ?token=).
 */
import type { Consent, Effective, Policy } from "./recordingNotice";
import type {
  EmbedSession,
  InterviewerReply,
  StreamEvent,
  SurveyBranding,
} from "./types";

/** Retries for a send the workflow refused as retryable (503 + Retry-After). */
const SEND_RETRIES = 3;
const RETRY_BASE_MS = 1000;

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export class ApiError extends Error {
  constructor(public status: number, detail: string) {
    super(detail);
  }
}

async function jsonOrThrow(resp: Response): Promise<any> {
  if (!resp.ok) {
    let detail = `${resp.status}`;
    try {
      detail = (await resp.json()).detail ?? detail;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(resp.status, String(detail));
  }
  return resp.json();
}

export interface RecordingConsentResult {
  status: number;
  code: string;
  body: { policy: Policy; consent: Consent; effective: Effective } | null;
}

export interface BootstrapRequest {
  publishable_key?: string;
  signed_token?: string;
  survey_id?: string;
  metadata?: Record<string, unknown>;
  resume_interview_id?: string;
  /** Prior session token proving possession of the resumed interview
   *  (accepted even recently expired). Resume is refused without it. */
  resume_session_token?: string;
  mode?: string;
}

export class ApiClient {
  private token = "";
  private expiresAt = 0;

  constructor(public baseUrl: string) {
    this.baseUrl = baseUrl.replace(/\/+$/, "");
  }

  get sessionToken(): string {
    return this.token;
  }

  adoptSession(session: EmbedSession): void {
    this.token = session.session_token;
    this.expiresAt = session.expires_at;
  }

  async bootstrap(req: BootstrapRequest): Promise<EmbedSession> {
    const resp = await fetch(`${this.baseUrl}/embed/sessions`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(req),
    });
    const session = (await jsonOrThrow(resp)) as EmbedSession;
    this.adoptSession(session);
    return session;
  }

  /** Refresh when inside the final 10 minutes of the token's lifetime. */
  async refreshIfNeeded(): Promise<void> {
    if (!this.token || this.expiresAt - Date.now() / 1000 > 600) return;
    const resp = await fetch(`${this.baseUrl}/embed/sessions/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ session_token: this.token }),
    });
    const data = await jsonOrThrow(resp);
    this.token = data.session_token;
    this.expiresAt = data.expires_at;
  }

  private authHeaders(): Record<string, string> {
    return this.token ? { Authorization: `Bearer ${this.token}` } : {};
  }

  /**
   * Submit an answer. The workflow rolls over (continue-as-new) every few
   * dozen turns and rejects sends for the moment that takes, which the API
   * reports as 503 + Retry-After. Resending the *same* message_id is
   * idempotent by contract, so ride that out here rather than showing the
   * visitor an error for something that fixes itself in a second. A 409 (the
   * interview is already over) is final and surfaces as an ApiError.
   */
  async sendMessage(
    interviewId: string,
    text: string,
    messageId = "",
    masked = false
  ): Promise<InterviewerReply> {
    const id = messageId || crypto.randomUUID();
    // `masked`: src/pii.ts already masked PII in `text`; the server leaves it be.
    const payload: Record<string, unknown> = { text, message_id: id };
    if (masked) payload.masked = true;
    for (let attempt = 1; ; attempt++) {
      await this.refreshIfNeeded();
      const resp = await fetch(
        `${this.baseUrl}/interviews/${interviewId}/messages`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", ...this.authHeaders() },
          body: JSON.stringify(payload),
        }
      );
      if (resp.status !== 503 || attempt > SEND_RETRIES) return jsonOrThrow(resp);
      await sleep(RETRY_BASE_MS * attempt);
    }
  }

  /**
   * Answer the contact step (contact.ts). The details go to their own
   * endpoint and are stored apart from the transcript. Never throws: a
   * refusal comes back as `{ ok: false, code }` (domain/contact.py ERR_*).
   */
  async submitContact(
    interviewId: string,
    body: { name?: string; email?: string; phone?: string; declined: boolean },
    messageId = ""
  ): Promise<{ ok: boolean; code?: string; status?: number; reply?: InterviewerReply }> {
    const id = messageId || crypto.randomUUID();
    try {
      for (let attempt = 1; ; attempt++) {
        await this.refreshIfNeeded();
        const resp = await fetch(`${this.baseUrl}/interviews/${interviewId}/contact`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...this.authHeaders() },
          body: JSON.stringify({ ...body, message_id: id }),
        });
        if (resp.status === 503 && attempt <= SEND_RETRIES) {
          await sleep(RETRY_BASE_MS * attempt);
          continue;
        }
        const data = await resp.json().catch(() => ({}));
        if (resp.ok) return { ok: true, reply: data as InterviewerReply };
        const code = data && typeof data.detail === "object" ? data.detail?.code : undefined;
        return { ok: false, code: code || "failed", status: resp.status };
      }
    } catch {
      return { ok: false, code: "failed" };
    }
  }

  /** Participant-facing branding for a survey — unauthenticated, and the
   *  only place a reloaded-after-completion widget can get a still-valid
   *  snowball-sampling invite (the interview's closing turns are long gone).
   *  Never throws: no branding just means no invite. */
  async getBranding(surveyId: string): Promise<SurveyBranding | null> {
    try {
      const resp = await fetch(`${this.baseUrl}/surveys/${surveyId}/branding`);
      if (!resp.ok) return null;
      return (await resp.json()) as SurveyBranding;
    } catch {
      return null;
    }
  }

  /** Switch the interview's language at the welcome step; resolves with the
   *  language now in effect. The workflow re-issues the welcome on the stream. */
  async setLanguage(interviewId: string, language: string): Promise<string> {
    await this.refreshIfNeeded();
    const resp = await fetch(`${this.baseUrl}/interviews/${interviewId}/language`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...this.authHeaders() },
      body: JSON.stringify({ language }),
    });
    return (await jsonOrThrow(resp)).language as string;
  }

  async getState(interviewId: string): Promise<any> {
    await this.refreshIfNeeded();
    const resp = await fetch(`${this.baseUrl}/interviews/${interviewId}`, {
      headers: this.authHeaders(),
    });
    return jsonOrThrow(resp);
  }

  async upload(interviewId: string, file: File): Promise<string> {
    await this.refreshIfNeeded();
    const form = new FormData();
    form.append("file", file);
    form.append("interview_id", interviewId);
    const resp = await fetch(`${this.baseUrl}/uploads`, {
      method: "POST",
      headers: this.authHeaders(),
      body: form,
    });
    return (await jsonOrThrow(resp)).url as string;
  }

  /**
   * The study's recording policy, `{audio, video}` each off | optional |
   * required (GET /surveys/{id}/recording, unauthenticated). Never throws:
   * any failure reads as off, so a lookup that fails never holds the
   * interview up.
   */
  async getRecordingPolicy(surveyId: string): Promise<Policy> {
    const off = { audio: "off", video: "off" };
    try {
      const resp = await fetch(
        `${this.baseUrl}/surveys/${encodeURIComponent(surveyId)}/recording`
      );
      if (!resp.ok) return off;
      const p = await resp.json();
      const mode = (v: unknown) => (v === "optional" || v === "required" ? v : "off");
      return { audio: mode(p?.audio), video: mode(p?.video) };
    } catch {
      return off;
    }
  }

  /** This interview's `{policy, consent, effective}`. Never throws: `body` is
   *  null on any failure, `status` 0 when the request got no answer. */
  async getRecordingConsent(interviewId: string): Promise<RecordingConsentResult> {
    try {
      await this.refreshIfNeeded();
      const resp = await fetch(`${this.baseUrl}/interviews/${interviewId}/recording-consent`, {
        headers: this.authHeaders(),
      });
      if (!resp.ok) return { status: resp.status, code: "", body: null };
      return { status: resp.status, code: "", body: await resp.json() };
    } catch {
      return { status: 0, code: "", body: null };
    }
  }

  /** Store the participant's answer. Never throws; a 409's reason (e.g.
   *  `already_decided`) comes back as `code`. */
  async postRecordingConsent(
    interviewId: string,
    choice: { audio?: boolean; video?: boolean }
  ): Promise<RecordingConsentResult> {
    try {
      await this.refreshIfNeeded();
      const resp = await fetch(`${this.baseUrl}/interviews/${interviewId}/recording-consent`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...this.authHeaders() },
        body: JSON.stringify(choice),
      });
      if (resp.ok) return { status: resp.status, code: "", body: await resp.json() };
      let code = "";
      try {
        code = (await resp.json())?.detail || "";
      } catch {
        /* not JSON */
      }
      return { status: resp.status, code: String(code), body: null };
    } catch {
      return { status: 0, code: "", body: null };
    }
  }

  /**
   * Subscribe to the interview's SSE stream. Reconnects with the last seen
   * offset; the final `utterance` event is authoritative over `delta`s.
   * Returns a disposer.
   */
  streamEvents(
    interviewId: string,
    fromOffset: number,
    onEvent: (event: StreamEvent) => void,
    onError?: (err: unknown) => void
  ): () => void {
    let disposed = false;
    let source: EventSource | null = null;
    let lastOffset = fromOffset;

    const connect = () => {
      if (disposed) return;
      const params = new URLSearchParams({ from_offset: String(lastOffset) });
      if (this.token) params.set("token", this.token);
      source = new EventSource(
        `${this.baseUrl}/interviews/${interviewId}/events?${params}`
      );
      for (const topic of [
        "utterance",
        "state",
        "question",
        "delta",
        "retry",
        "abandon",
      ]) {
        source.addEventListener(topic, (ev) => {
          const msg = ev as MessageEvent;
          const offset = Number(msg.lastEventId || 0);
          // Resume from the *next* offset after the last delivered event.
          if (offset) lastOffset = offset + 1;
          try {
            onEvent({ topic, offset, data: JSON.parse(msg.data) });
          } catch (err) {
            onError?.(err);
          }
        });
      }
      source.onerror = () => {
        // EventSource auto-reconnects, but not with our offset param —
        // rebuild the connection ourselves after a short backoff.
        source?.close();
        if (!disposed) setTimeout(connect, 2000);
      };
    };
    connect();
    return () => {
      disposed = true;
      source?.close();
    };
  }

  /** The study's welcome read aloud (POST /tts/welcome), as WAV bytes: the
   *  voice start screen's Listen button, before any interview exists. */
  async welcomeSpeech(surveyId: string, language?: string): Promise<ArrayBuffer> {
    const res = await fetch(`${this.baseUrl}/tts/welcome`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ survey_id: surveyId, language }),
    });
    if (!res.ok) throw new ApiError(res.status, await res.text());
    return res.arrayBuffer();
  }

  voiceSocketUrl(interviewId: string, fromOffset = 0): string {
    const ws = this.baseUrl.replace(/^http/, "ws");
    // The welcome step is answered on the start screen (widget.ts
    // voiceStartScreen), so the gateway shows it without speaking it.
    const params = new URLSearchParams({
      from_offset: String(fromOffset),
      silent_welcome: "true",
    });
    if (this.token) params.set("token", this.token);
    return `${ws}/interviews/${interviewId}/voice?${params}`;
  }
}
